import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/apiConfig';
import ProductStatusChangeReport from './components/ProductStatusChangeReport';
import { Button } from '../../components/ui';
import { ArrowLeftIcon } from '../../components/ui/Icons';
import { PrinterIcon } from '../../components/ui/Icons';

/**
 * ProductStatusChangePrintPage - Standalone page for printing product status change reports
 * Accessible via /product-status-change/:id/print
 */
const ProductStatusChangePrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productStatusChange, setProductStatusChange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductStatusChange = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        // Fetch product status change
        const response = await fetch(`${API_BASE_URL}/product-status-change/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch product status change');
        }

        const data = await response.json();
        setProductStatusChange(data);
      } catch (err) {
        console.error('Error fetching product status change:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProductStatusChange();
    }
  }, [id]);

  const handlePrint = () => {
    // Small delay to ensure content is fully rendered
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Auto-print when page loads (optional - can be removed if not desired)
  useEffect(() => {
    if (productStatusChange && !loading && !error) {
      // Auto-print after a short delay to ensure content is rendered
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [productStatusChange, loading, error]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading product status change data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!productStatusChange) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="mb-4">Product status change not found</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-status-change-report-wrapper min-h-screen bg-gray-100">
      {/* Print Controls - Hidden when printing */}
      <div className="no-print bg-white border-b shadow-sm p-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={handleBack} variant="outline" size="sm">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-semibold">Product Status Change Report - ID: {productStatusChange.id}</h1>
          </div>
          <Button onClick={handlePrint} variant="primary" size="sm">
            <PrinterIcon className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Report Content */}
      <div className="py-4" style={{ minHeight: 'auto', height: 'auto', overflow: 'visible', background: 'white' }}>
        {productStatusChange ? (
          <ProductStatusChangeReport productStatusChange={productStatusChange} />
        ) : (
          <div>Loading product status change data...</div>
        )}
      </div>
    </div>
  );
};

export default ProductStatusChangePrintPage;

